/**
 * Manuell bedömning av lägenheter som "kunde inte kontrolleras".
 *
 * I Avvikelser och Inneboende flyttar man en FOLKBOKFÖRD PERSON. En lägenhet
 * som inte gick att kontrollera har ingen sådan — raden finns just för att
 * ingen hittades på numret. Där bedöms i stället hela raden: medlemmen och
 * lägenheten flyttas till Godkänd eller Inneboende.
 *
 * Nyckeln bygger på adress + lägenhetsnummer + medlem. Bara lägenhetsnummer
 * räcker inte: flera av de här raderna saknar nummer helt.
 *
 * Spegel av functions/src/utils/auditManualVerdict.ts. Håll dem identiska.
 */
import { isUncheckedVerdict } from './auditSummary';

export const MANUAL_VERDICTS = ['GODKAND', 'INNEBOENDE'];

const norm = (v) => String(v ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
const normApartment = (v) => norm(v).replace(/lgh/g, '').replace(/\s+/g, '');

export function isManualVerdict(verdict) {
  return MANUAL_VERDICTS.includes(verdict);
}

export function manualVerdictMemberName(row) {
  const m = row.member || {};
  return norm(m.legalEntity) || norm(`${m.firstName ?? ''} ${m.lastName ?? ''}`);
}

export function manualVerdictKey(row) {
  return `${norm(row.address)}|${normApartment(row.apartmentNumber)}|${manualVerdictMemberName(row)}`;
}

/**
 * Lägger de manuella bedömningarna ovanpå en körnings resultat. Bara rader som
 * inte gick att kontrollera berörs. Ursprungligt utfall och skäl finns kvar på
 * raden, så det alltid syns att bedömningen är gjord för hand.
 */
export function applyManualVerdicts(results, manual) {
  const map = manual || {};
  return (results || []).map((row) => {
    if (!isUncheckedVerdict(row.verdict)) return row;
    const verdict = map[manualVerdictKey(row)];
    if (!isManualVerdict(verdict)) return row;
    return { ...row, verdict, manualVerdict: true, originalVerdict: row.verdict };
  });
}

/**
 * En sparad körning kan redan bära manuella bedömningar (de läggs på i
 * runSubletAudit). För att kunna ångra dem i vyn delas körningen upp igen:
 * grundresultatet som det såg ut före bedömningarna, och bedömningarna för sig.
 */
export function splitManualVerdicts(results) {
  const manual = {};
  const base = (results || []).map((row) => {
    if (!row.manualVerdict) return row;
    manual[manualVerdictKey(row)] = row.verdict;
    const { manualVerdict, originalVerdict, ...rest } = row;
    return { ...rest, verdict: originalVerdict };
  });
  return { base, manual };
}

/** Rad-bedömningarna ur auditOverrides. Person-flyttar (utan kind) hoppas över. */
export function manualVerdictsFromOverrides(overrides) {
  const manual = {};
  (overrides || []).forEach((o) => {
    if (o && o.kind === 'member' && o.memberKey && isManualVerdict(o.overrideVerdict)) {
      manual[o.memberKey] = o.overrideVerdict;
    }
  });
  return manual;
}

/**
 * Det sparade är sanningen. En sparad körning kan bära en bedömning som sedan
 * ångrats — den får då 'RESET', så raden inte dyker upp som bedömd igen.
 */
export function reconcileManualVerdicts(reportResults, saved) {
  const { manual: embedded } = splitManualVerdicts(reportResults);
  const merged = { ...(saved || {}) };
  Object.keys(embedded).forEach((key) => {
    if (!(key in merged)) merged[key] = 'RESET';
  });
  return merged;
}
